import {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Skeleton,
    IconButton,
    Stack,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Chip,
    OutlinedInput,
    Select,
    InputLabel,
    FormControl,
    FormHelperText,
    SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTheme} from '@mui/material/styles';
import {AuditHkfrsSmeFrsService} from '@/services/auditService';
import {SmeFrsCreateBO, SmeFrsUpdateBO, SmeFrsParentInfoVO} from '@/services/auditService/auditHkfrsSmeFrsService';
import {FsCategoryService} from '@/services/accountService';
import {InputField} from '@/components/common';

interface Props {
    open: boolean;
    onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
    smeFrsId?: string;
}

// no 字段校验：必须是数字、英文小写字母或罗马数字的其中之一
const NO_PATTERN = /^[0-9a-z]+$|^[ivxlcdmIVXLCDM]+$/;

export default function SmeFrsDetail({open, onClose, smeFrsId}: Props) {
    const theme = useTheme();
    const isEdit = !!smeFrsId;
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState<{
        pid: string;
        no: string;
        content: string;
        code: string[];
        isDel: number;
    }>({
        pid: '',
        no: '',
        content: '',
        code: [],
        isDel: 0,
    });

    const [fieldErrors, setFieldErrors] = useState<{
        pid?: string;
        no?: string;
        content?: string;
        _global?: string;
    }>({});

    // 父节点下拉选项
    const [parentOptions, setParentOptions] = useState<SmeFrsParentInfoVO[]>([]);
    // Working Paper 下拉选项 (code → name)
    const [wpOptions, setWpOptions] = useState<Record<string, string>>({});

    // 加载详情
    const loadDetail = async () => {
        if (!smeFrsId) return;
        try {
            setLoading(true);
            const data = await AuditHkfrsSmeFrsService.getById(smeFrsId);
            setForm({
                pid: data.pid ?? '',
                no: data.no ?? '',
                content: data.content ?? '',
                code: data.code ?? [],
                isDel: data.isDel ?? 0,
            });
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            if (isEdit) {
                loadDetail();
            } else {
                setForm({pid: '', no: '', content: '', code: [], isDel: 0});
            }
            setFieldErrors({});

            // 加载父节点选项
            AuditHkfrsSmeFrsService.getParentInfoList(smeFrsId)
                .then(res => setParentOptions(res || []))
                .catch(() => setParentOptions([]));

            // 加载 Working Paper 选项
            FsCategoryService.queryFsCategorySelection()
                .then(res => setWpOptions(res || {}))
                .catch(() => setWpOptions({}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, smeFrsId]);

    const validate = (): boolean => {
        const errors: typeof fieldErrors = {};
        if (!form.pid) errors.pid = 'Parent Content is required';
        if (!form.no) {
            errors.no = 'No is required';
        } else if (!NO_PATTERN.test(form.no)) {
            errors.no = 'No must be a number, lowercase letter, or roman numeral';
        }
        if (!form.content) errors.content = 'Content is required';
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const isRoot = form.pid ? 0 : 1;
            if (isEdit && smeFrsId) {
                const bo: SmeFrsUpdateBO = {
                    id: smeFrsId,
                    pid: form.pid || undefined,
                    no: form.no,
                    content: form.content,
                    code: form.code,
                    isRoot,
                    isDel: form.isDel,
                };
                await AuditHkfrsSmeFrsService.update(bo);
            } else {
                const bo: SmeFrsCreateBO = {
                    pid: form.pid || undefined,
                    no: form.no,
                    content: form.content,
                    code: form.code,
                    isRoot,
                    isDel: form.isDel,
                };
                await AuditHkfrsSmeFrsService.create(bo);
            }
            onClose({refreshed: true, message: 'Saved successfully', severity: 'success'});
        } catch (e: any) {
            const msg = e?.message || 'Save failed';
            setFieldErrors({_global: msg});
        } finally {
            setSaving(false);
        }
    };

    // Working Paper 多选处理
    const handleWpChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setForm({...form, code: typeof value === 'string' ? value.split(',') : value});
    };

    return (
        <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isEdit ? 'Edit SME-FRS' : 'Create SME-FRS'}
                    <IconButton size="small" onClick={() => onClose()}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Stack spacing={2} mt={1}>
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={40}/>)}
                    </Stack>
                ) : (
                    <Stack spacing={2} mt={1}>
                        {/* 2.2.1 parentContent 下拉 */}
                        <InputField
                            size="small"
                            label="Parent Content"
                            select
                            required
                            error={!!fieldErrors.pid}
                            helperText={fieldErrors.pid || ''}
                            value={form.pid}
                            onChange={(e) => setForm({...form, pid: e.target.value})}
                        >
                            <MenuItem value="">None (Root)</MenuItem>
                            {parentOptions.map((opt) => (
                                <MenuItem key={opt.id} value={opt.id ?? ''}>
                                    {opt.no} - {opt.content}
                                </MenuItem>
                            ))}
                        </InputField>

                        {/* 2.2.2 no */}
                        <InputField
                            size="small"
                            label="No"
                            required
                            error={!!fieldErrors.no}
                            helperText={fieldErrors.no || ''}
                            value={form.no}
                            onChange={(e) => setForm({...form, no: e.target.value})}
                        />

                        {/* 2.2.3 content */}
                        <InputField
                            size="small"
                            label="Content"
                            required
                            multiline
                            minRows={2}
                            error={!!fieldErrors.content}
                            helperText={fieldErrors.content || ''}
                            value={form.content}
                            onChange={(e) => setForm({...form, content: e.target.value})}
                        />

                        {/* 2.2.4 working paper 多选 */}
                        <FormControl size="small" fullWidth>
                            <InputLabel id="wp-select-label">Working Paper</InputLabel>
                            <Select
                                labelId="wp-select-label"
                                multiple
                                value={form.code}
                                onChange={handleWpChange}
                                input={<OutlinedInput label="Working Paper"/>}
                                renderValue={(selected) => (
                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                        {selected.map((code) => (
                                            <Chip key={code} label={wpOptions[code] || code} size="small"/>
                                        ))}
                                    </Box>
                                )}
                            >
                                {Object.entries(wpOptions).map(([code, name]) => (
                                    <MenuItem key={code} value={code}>
                                        <Checkbox size="small" checked={form.code.indexOf(code) > -1}/>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* 2.2.5 isDel */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={form.isDel === 1}
                                    onChange={(e) => setForm({...form, isDel: e.target.checked ? 1 : 0})}
                                />
                            }
                            label="Is Deleted"
                        />

                        {fieldErrors._global && (
                            <FormHelperText error>{fieldErrors._global}</FormHelperText>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={saving || loading}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
